<script setup>
import InputLabel from "@/Components/InputLabel.vue";
import InputError from "@/Components/InputError.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import '@vuepic/vue-datepicker/dist/main.css'
import Datepicker from "@vuepic/vue-datepicker";
import dayjs from "dayjs";
import {useForm, usePage} from "@inertiajs/inertia-vue3";
import {computed, ref, watch} from "vue";
import {useHelpers, useTenantButtonVisibility, useTenantTextRules} from "@/helpers";

const props = defineProps({
  hideSave: {
    type: Boolean,
    default() {
      return false;
    }
  },
  isGarmentColorPricingView: {
    type: Boolean,
    default: false,
  },
});

const helpers = useHelpers();
const { isButtonVisible } = useTenantButtonVisibility();
const { resolveTextRule } = useTenantTextRules();
const currentUser = usePage().props.value.auth.user;
const projectProperty = computed(() => usePage().props.value.project);
let project = projectProperty.value;
watch(projectProperty, (value) => {
  resetForm(value);
  project = projectProperty.value;
});

const minDate = dayjs().toDate();

const lapDip = ref(project?.milestone?.lapdip);
const lapDipApproval = ref(project?.milestone?.lapdipapproval);
const estimatedDelivery = ref(project?.milestone?.estdelivery || null);
const sample = ref(project?.milestone?.prdtnsample);
const sampleApproval = ref(project?.milestone?.prdtnsampleapprval);
const sizingLine = ref(project?.milestone?.szngline);
const targetDelivery = ref(project?.milestone?.prdtntrgtdelvry);
const designBoard = ref(project?.milestone?.desbordcmplt);
const designBoardCalendar = ref(project?.milestone?.design_board_calender);

const form = useForm({
  _method: 'POST',
  milestone: {
    lapdip: null,
    lapdipapproval: null,
    estdelivery: null,
    prdtnsample: null,
    prdtnsampleapprval: null,
    szngline: null,
    prdtntrgtdelvry: null,
    desbordcmplt: null,
    desbordappval: project?.milestone?.desbordappval || 0,
    design_board_calender: null
  }
});

const saving = ref(false);
const saveForm = () => {
  saving.value = true;
  form._method = 'PUT';
  form.milestone.lapdip = lapDip.value;
  form.milestone.lapdipapproval = lapDipApproval.value;
  form.milestone.estdelivery = estimatedDelivery.value;
  form.milestone.prdtnsample = sample.value;
  form.milestone.prdtnsampleapprval = sampleApproval.value;
  form.milestone.szngline = sizingLine.value;
  form.milestone.prdtntrgtdelvry = targetDelivery.value;
  form.milestone.desbordcmplt = designBoard.value;
  form.milestone.design_board_calender = designBoardCalendar.value;

  form.post(route('admin.projects.update', {project: project.pid}), {
    preserveScroll: true,
    onFinish() {
      saving.value = false;
    }
  });
  saving.value = false;
}

const resetForm = (projectData) => {
  if (Object.keys(usePage().props.value.errors).length > 0) {
    return;
  }

  lapDip.value = projectData.milestone.lapdip;
  lapDipApproval.value = projectData.milestone.lapdipapproval;
  estimatedDelivery.value = projectData.milestone.estdelivery;
  sample.value = projectData.milestone.prdtnsample;
  sampleApproval.value = projectData.milestone.prdtnsampleapprval;
  sizingLine.value = projectData.milestone.szngline;
  targetDelivery.value = projectData.milestone.prdtntrgtdelvry;
}
const lapDipLabel = computed(() => resolveTextRule('project.milestone.lapdip', 'Lap Dip', 'project.milestone.lapdip'));
const lapDipApprovalLabel = computed(() => resolveTextRule('project.milestone.lapdipapproval', 'Lap Dip Approval', 'project.milestone.lapdipapproval'));
const estimatedDeliveryLabel = computed(() => resolveTextRule('project.milestone.estdelivery', 'Estimated Fabric Delivery Date', 'project.milestone.estdelivery'));
const productionSampleLabel = computed(() => resolveTextRule('project.milestone.prdtnsample', 'Production Sample', 'project.milestone.prdtnsample'));
const productionSampleApprovalLabel = computed(() => resolveTextRule('project.milestone.prdtnsampleapprval', 'Production Sample Approval', 'project.milestone.prdtnsampleapprval'));
const sizingLineLabel = computed(() => resolveTextRule('project.milestone.szngline', 'Sizing Line', 'project.milestone.szngline'));
const targetDeliveryLabel = computed(() => resolveTextRule('project.milestone.prdtntrgtdelvry', 'Production Target Delivery', 'project.milestone.prdtntrgtdelvry'));
const designBoardCompletionLabel = computed(() => resolveTextRule('project.milestone.desbordcmplt', 'Design Board Completion', 'project.milestone.desbordcmplt'));
const designBoardApprovalLabel = computed(() => resolveTextRule('project.milestone.desbordappval', 'Design Board Approval', 'project.milestone.desbordappval'));
const isTargetDeliveryRequired = computed(() => props.isGarmentColorPricingView);
</script>

<template>
  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md">
    <div class="sm:grid grid-cols-6 gap-6">
      <!-- Lap Dip -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="lap-dip" :value="lapDipLabel"/>
        <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="lapDip" format="MMMM d, yyyy"
                    :min-date="minDate" id="lap-dip"
                    :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
                    model-type="MM/dd/yyyy" auto-apply :close-on-auto-apply="true" :enable-time-picker="false"/>
        <InputError class="mt-2" :message="form.errors['milestone.lapdip']"/>
      </div>
      <!-- Lap Dip Approval -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="lap-dip" :value="lapDipApprovalLabel"/>
        <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="lapDipApproval" format="MMMM d, yyyy"
                    :min-date="minDate"
                    :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
                    model-type="MM/dd/yyyy" auto-apply :close-on-auto-apply="true" :enable-time-picker="false"/>
        <InputError class="mt-2" :message="form.errors['milestone.lapdipapproval']"/>
      </div>
      <!-- Estimated Delivery -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="estimated-delivery" :value="estimatedDeliveryLabel" :required="isTargetDeliveryRequired"/>
        <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="estimatedDelivery" format="MMMM d, yyyy"
                    :min-date="minDate" id="estimated-delivery"
                    :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
                    model-type="MM/dd/yyyy" auto-apply :close-on-auto-apply="true" :enable-time-picker="false"/>
        <InputError class="mt-2" :message="form.errors['milestone.estdelivery']"/>
      </div>
      <!-- Sample -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="lap-dip" :value="productionSampleLabel"/>
        <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="sample" format="MMMM d, yyyy"
                    :min-date="minDate"
                    :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
                    model-type="MM/dd/yyyy" auto-apply :close-on-auto-apply="true" :enable-time-picker="false"/>
        <InputError class="mt-2" :message="form.errors['milestone.prdtnsample']"/>
      </div>
      <!-- Sample Approval -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="lap-dip" :value="productionSampleApprovalLabel"/>
        <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="sampleApproval" format="MMMM d, yyyy"
                    :min-date="minDate"
                    :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
                    model-type="MM/dd/yyyy" auto-apply :close-on-auto-apply="true" :enable-time-picker="false"/>
        <InputError class="mt-2" :message="form.errors['milestone.prdtnsampleapprval']"/>
      </div>
      <!-- Sizing Line -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="lap-dip" :value="sizingLineLabel"/>
        <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="sizingLine" format="MMMM d, yyyy"
                    :min-date="minDate"
                    :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
                    model-type="MM/dd/yyyy" auto-apply :close-on-auto-apply="true" :enable-time-picker="false"/>
        <InputError class="mt-2" :message="form.errors['milestone.szngline']"/>
      </div>
      <!-- Production target Delivery -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="target-delivery" :value="targetDeliveryLabel"/>
        <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="targetDelivery" format="MMMM d, yyyy"
                    :min-date="minDate" id="target-delivery"
                    :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
                    model-type="MM/dd/yyyy" auto-apply :close-on-auto-apply="true" :enable-time-picker="false"/>
        <InputError class="mt-2" :message="form.errors['milestone.prdtntrgtdelvry']"/>
      </div>
      <!-- Design Board Completion -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="lap-dip" :value="designBoardCompletionLabel"/>
        <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="designBoard" format="MMMM d, yyyy"
                    :min-date="minDate"
                    :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
                    model-type="MM/dd/yyyy" auto-apply :close-on-auto-apply="true" :enable-time-picker="false"/>
        <InputError class="mt-2" :message="form.errors['milestone.desbordcmplt']"/>
      </div>
      <!-- Design Board Completion -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel :value="designBoardApprovalLabel"/>
        <label for="design-board-yes" class="mr-5">
          <input type="radio" v-model="form.milestone.desbordappval" :value="1" id="design-board-yes"
                 :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))">
          <span class="ml-1">Yes</span>
        </label>
        <label for="design-board-no">
          <input type="radio" v-model="form.milestone.desbordappval" :value="0" id="design-board-no"
                 :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))">
          <span class="ml-1">No</span>
        </label>

        <InputError class="mt-2" :message="form.errors['milestone.desbordcmplt']"/>
      </div>
      <!-- Design Board Calender -->
      <div
        class="col-span-6 sm:col-span-4"
        v-if="helpers.userCanManageProject(currentUser) && isButtonVisible('project.edit.production_milestone.design_board_calendar')"
      >
        <InputLabel for="lap-dip" value="Design Board Calender"/>
        <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="designBoardCalendar" format="MMMM d, yyyy"
                    :min-date="minDate"
                    :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
                    model-type="MM/dd/yyyy" auto-apply :close-on-auto-apply="true" :enable-time-picker="false"/>
        <InputError class="mt-2" :message="form.errors['milestone.design_board_calender']"/>
      </div>
    </div>
  </div>

  <div
      class="flex items-center justify-center px-4 py-3 bg-gray-50 text-right sm:px-6 shadow sm:rounded-bl-md sm:rounded-br-md sticky bottom-0">
    <PrimaryButton @click.prevent="saveForm()" :disabled="saving" v-if="hideSave === false">Save</PrimaryButton>
  </div>
</template>
